import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import Avatar from "react-avatar";
import ToastDefault from "../../components/Toast";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Footer from "../../components/Footer";
import { getAuth } from "../../utils/Helper";
import {
  UpdateProfileAction,
  ChangePasswordAction,
} from "../../redux/actions/UserActions";
import "./dashboard.css";

type SeoContent = {
  meta_title: string;
  meta_description?: string;
};

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [soeContent, setSoeContent] = useState<SeoContent | null>(null);
  const auth = getAuth();

  const fullName = auth?.name || "";

  // Profile form state
  const [profile, setProfile] = useState({
    name: fullName || "",
    email: auth?.email || "",
    phone: auth?.mobile_number || "",
    bio: auth?.about || "",
  });
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>(
    {}
  );

  // Password form state
  const [passwordData, setPasswordData] = useState({
    old_password: "",
    password: "",
    confirm_password: "",
  });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>(
    {}
  );

  const [loadingM, setLoadingM] = useState(false);
  const { status, data, error } = useSelector(
    (state: any) => state.updateProfileState
  );
  const { status_password, data_password, error_password } = useSelector(
    (state: any) => state.changePasswordState
  );

  // Live password requirement checks
  const requirements = {
    length: passwordData.password.length >= 8,
    uppercase: /[A-Z]/.test(passwordData.password),
    lowercase: /[a-z]/.test(passwordData.password),
    number: /\d/.test(passwordData.password),
    special: /[@$!%*?&]/.test(passwordData.password),
  };

  // Profile validation
  const validateProfile = () => {
    setLoadingM(true);
    const errors: Record<string, string> = {};
    if (!profile.name.trim()) errors.name = "Name is required.";
    if (!profile.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      errors.email = "Invalid email format.";
    }
    if (!profile.phone.trim()) errors.phone = "Phone number is required.";
    return errors;
  };

  // Password validation
  const validatePassword = () => {
    setLoadingM(true);
    const errors: Record<string, string> = {};
    if (!passwordData.old_password)
      errors.old_password = "Old password is required.";
    if (!passwordData.password) {
      errors.password = "Password is required.";
    } else if (passwordData.password.length < 8) {
      errors.password = "Password must be at least 8 characters.";
    }
    if (
      passwordData.confirm_password &&
      passwordData.confirm_password !== passwordData.password
    ) {
      errors.confirm_password = "Passwords do not match.";
    }
    return errors;
  };

  const handleProfileSubmit = (e: React.FormEvent) => {
    setLoadingM(true);
    e.preventDefault();
    const errors = validateProfile();
    setProfileErrors(errors);
    if (Object.keys(errors).length === 0) {
      dispatch(UpdateProfileAction(profile));
    } else {
      setTimeout(() => {
        setLoadingM(false);
      }, 200);
    }
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    setLoadingM(true);
    e.preventDefault();
    const errors = validatePassword();
    setPasswordErrors(errors);
    if (Object.keys(errors).length === 0) {
      dispatch(ChangePasswordAction(passwordData));
    } else {
      setTimeout(() => {
        setLoadingM(false);
      }, 200);
    }
  };

  // auto clear profile error when user types
  const handleProfileChange = (field: string, value: string) => {
    setProfile({ ...profile, [field]: value });
    setProfileErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // auto clear password error when user types
  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData({ ...passwordData, [field]: value });

    // clear that field's error immediately
    setPasswordErrors((prev) => ({ ...prev, [field]: "" }));

    // Special case: clear confirm password error if passwords now match
    if (field === "password" || field === "confirm_password") {
      if (
        (field === "password" && value === passwordData.confirm_password) ||
        (field === "confirm_password" && value === passwordData.password)
      ) {
        setPasswordErrors((prev) => ({ ...prev, confirm_password: "" }));
      }
    }
  };

  useEffect(() => {
    if (status) {
      if (data?.data) {
        localStorage.setItem("user-data", JSON.stringify(data?.data));
        dispatch(UpdateProfileAction("RESET"));
        toast.success(data?.message);
        setLoadingM(false);
      }
    }

    if (error) {
      dispatch(UpdateProfileAction("RESET"));
      setLoadingM(false);
    }
  }, [dispatch, status, error, data]);

  useEffect(() => {
    if (status_password) {
      dispatch(ChangePasswordAction("RESET"));
      toast.success(data_password?.message);
      setLoadingM(false);
      setPasswordData({
        old_password: "",
        password: "",
        confirm_password: "",
      });
    }
    if (error_password) {
      dispatch(ChangePasswordAction("RESET"));
      setLoadingM(false);
    }
  }, [dispatch, status_password, error_password]);

  return (
    <>
      <Helmet>
        <title>{`${soeContent?.meta_title || "Dashboard"}`}</title>
        <meta
          name="title"
          content={soeContent?.meta_title || "E-learning Platform"}
        />
        <meta
          name="description"
          content={
            soeContent?.meta_description ||
            "Learn with curated e-learning resources"
          }
        />
      </Helmet>

      <Header />

      <div className="container py-5">
        <div className="row">
          <Sidebar />

          <div className="col-lg-9">
            <div className="main-content">
              {/* Profile Section */}
              <div className="profile-header">
                <Avatar
                  name={auth?.name}
                  size="85"
                  round={true}
                  color="#3568B2"
                  fgColor="#fff"
                />
                <div className="profile-info">
                  <h1 className="profile-name">{auth?.name}</h1>
                  <p className="profile-email">{auth?.email}</p>
                </div>
              </div>

              <ul className="nav nav-tabs" id="accountTabs" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link active"
                    id="profile-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#profile"
                    type="button"
                    role="tab"
                  >
                    Profile
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="password-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#password"
                    type="button"
                    role="tab"
                  >
                    Password
                  </button>
                </li>
              </ul>

              <div className="tab-content" id="accountTabsContent">
                {/* Profile Tab */}
                <div
                  className="tab-pane fade show active"
                  id="profile"
                  role="tabpanel"
                >
                  <form onSubmit={handleProfileSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Full Name</label>
                      <input
                        type="text"
                        className={`form-control ${
                          profileErrors.name ? "is-invalid" : ""
                        }`}
                        value={profile.name}
                        onChange={(e) =>
                          handleProfileChange("name", e.target.value)
                        }
                      />
                      {profileErrors.name && (
                        <div className="invalid-feedback">
                          {profileErrors.name}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        className={`form-control ${
                          profileErrors.email ? "is-invalid" : ""
                        }`}
                        value={profile.email}
                        onChange={(e) =>
                          handleProfileChange("email", e.target.value)
                        }
                      />
                      {profileErrors.email && (
                        <div className="invalid-feedback">
                          {profileErrors.email}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        className={`form-control ${
                          profileErrors.phone ? "is-invalid" : ""
                        }`}
                        value={profile.phone}
                        onChange={(e) =>
                          handleProfileChange("phone", e.target.value)
                        }
                      />
                      {profileErrors.phone && (
                        <div className="invalid-feedback">
                          {profileErrors.phone}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Bio</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={profile.bio}
                        onChange={(e) =>
                          handleProfileChange("bio", e.target.value)
                        }
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loadingM}
                    >
                      {loadingM ? "Updating..." : "Update Profile"}
                    </button>
                  </form>
                </div>

                {/* Password Tab */}
                <div className="tab-pane fade" id="password" role="tabpanel">
                  <form onSubmit={handlePasswordSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Current Password</label>
                      <input
                        type="password"
                        className={`form-control ${
                          passwordErrors.old_password ? "is-invalid" : ""
                        }`}
                        value={passwordData.old_password}
                        onChange={(e) =>
                          handlePasswordChange("old_password", e.target.value)
                        }
                      />
                      {passwordErrors.old_password && (
                        <div className="invalid-feedback">
                          {passwordErrors.old_password}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        className={`form-control ${
                          passwordErrors.password ? "is-invalid" : ""
                        }`}
                        value={passwordData.password}
                        onChange={(e) =>
                          handlePasswordChange("password", e.target.value)
                        }
                      />
                      {passwordErrors.password && (
                        <div className="invalid-feedback">
                          {passwordErrors.password}
                        </div>
                      )}
                      <div className="password-requirements mt-1">
                        <small className="text-muted">Must have at least 8 characters, 1 number, and 1 special character.</small>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Confirm New Password</label>
                      <input
                        type="password"
                        className={`form-control ${
                          passwordErrors.confirm_password ? "is-invalid" : ""
                        }`}
                        value={passwordData.confirm_password}
                        onChange={(e) =>
                          handlePasswordChange(
                            "confirm_password",
                            e.target.value
                          )
                        }
                      />
                      {passwordErrors.confirm_password && (
                        <div className="invalid-feedback">
                          {passwordErrors.confirm_password}
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loadingM}
                    >
                      {loadingM ? "Changing..." : "Change Password"}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastDefault />
      <Footer />
    </>
  );
};

export default Dashboard;
